import React, { Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';

import { ContractDeal, Entity } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import clockTokens from '@amzn/meridian-tokens/base/icon/clock';
import searchTokens from '@amzn/meridian-tokens/base/icon/search';
import deleteTokens from '@amzn/meridian-tokens/base/icon/trash';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tooltip from '@amzn/meridian/tooltip';

import { isNil, isUndefined } from 'lodash';
import { SearchResultTable, SelectionRow } from 'src/components/CompareContracts/ContractSearchModal/SearchResultTable';
import { DataSelector } from 'src/components/CompareContracts/DataSelector/data-selector';
import { TimestampPicker, formatDate } from 'src/components/CompareContracts/TimestampPicker/TimestampPicker';
import { AppContext } from 'src/contexts/appContext';
import { IMetadata } from 'src/types/IMetadata';

interface IContractSearchModal {
    entities?: Entity[];
    deals?: ContractDeal[];
    contractIdMap: Map<string, string>;
    setContractId: Dispatch<SetStateAction<string | undefined>>;
}

export const ContractSearchModal: React.FC<IContractSearchModal> = (props) => {
    const { contractIdMap } = props;
    const { metadata } = useContext(AppContext);
    const [open, setOpen] = useState(false);

    const [entityId, setEntityId] = useState<string | undefined>();
    const [serviceId, setServiceId] = useState<number | undefined>();
    const [timestamp, setTimestamp] = useState<string | undefined>();

    const entityIdMap = useMemo(() => createEntityIdMap(props.entities), [props.entities]);
    const entityContractMap = useMemo(() => createEntityContractMap(props.deals), [props.deals]);
    const filteredEntityIdMap = useMemo(
        () => removeEntitiesWithoutDeals(entityIdMap, entityContractMap),
        [entityIdMap, entityContractMap]
    );
    const serviceIdMap = useMemo(() => createServiceIdMap(metadata), [metadata.serviceMapping]);

    const needsInput = isNil(entityId);

    const options = useMemo(() => {
        if (needsInput) {
            return [];
        } else {
            return createSelections(entityContractMap, contractIdMap, entityIdMap, serviceIdMap);
        }
    }, [needsInput, entityContractMap, contractIdMap, entityIdMap, serviceIdMap]);

    const filteredOptions = useMemo(() => {
        return filterSelections(options, entityId, serviceId, timestamp);
    }, [options, entityId, serviceId, timestamp]);

    const selectOption = useCallback(
        (action: SetStateAction<string | undefined>) => {
            props.setContractId(action);
            setOpen(false);
        },
        [props.setContractId]
    );

    const setTimestampToNow = useCallback(() => {
        setTimestamp(formatDate(new Date(Date.now())));
    }, [setTimestamp]);

    return (
        <>
            <Button onClick={() => setOpen(true)} aria-label={'open-contract-search'}>
                <Icon tokens={searchTokens} />
            </Button>
            <Modal title={'Find Contract'} open={open} onClose={() => setOpen(false)}>
                <Row alignmentVertical={'top'}>
                    <Column>
                        <DataSelector
                            id={entityId}
                            setId={setEntityId}
                            idMap={filteredEntityIdMap}
                            title={'Label'}
                            prefix={`Label`}
                        />
                        <Row widths={['fit', 'fill']}>
                            <Tooltip position={'end'} title={'Clear service'}>
                                <Button
                                    onClick={() => setServiceId(undefined)}
                                    type={'tertiary'}
                                    aria-label={`clear-service`}
                                >
                                    <Icon tokens={deleteTokens} />
                                </Button>
                            </Tooltip>
                            <Select
                                prefix={'Service'}
                                value={serviceId}
                                onChange={setServiceId}
                                placeholder={'Select Service...'}
                            >
                                {Array.from(serviceIdMap.entries()).map(([serviceId, serviceName]) => (
                                    <SelectOption label={serviceName} value={serviceId} key={serviceId} />
                                ))}
                            </Select>
                        </Row>
                        <TimestampPicker timestamp={timestamp} setTimestamp={setTimestamp} />
                        <Row>
                            <Button onClick={setTimestampToNow} type={'tertiary'}>
                                Use current date
                                <Icon tokens={clockTokens} />
                            </Button>
                        </Row>
                    </Column>
                    <Column>
                        <Alert size={'medium'} type={needsInput ? 'warning' : 'neutral'}>
                            {needsInput ? 'Please select a label' : `Found ${filteredOptions.length} deals`}
                        </Alert>
                        <SearchResultTable results={filteredOptions} setContractId={selectOption} />
                    </Column>
                </Row>
            </Modal>
        </>
    );
};

export function createServiceIdMap(metadata: IMetadata): Map<number, string> {
    const result = new Map<number, string>();

    Object.keys(metadata.serviceMapping)
        .map((key) => Number.parseInt(key))
        .filter((serviceId) => !isNaN(serviceId))
        .filter((serviceId) => serviceId > 0)
        .forEach((serviceId) => {
            const code = metadata.serviceMapping[serviceId]?.serviceName;
            if (!isUndefined(code)) {
                result.set(serviceId, code);
            }
        });

    return result;
}

function createEntityIdMap(entities?: Entity[]): Map<string, string> {
    const map = new Map<string, string>();
    entities?.forEach((entity) => {
        if (!isUndefined(entity.entityId) && !isUndefined(entity.entityName)) {
            map.set(`${entity.entityId}`, entity.entityName);
        }
    });
    return map;
}

type Interval = {
    contractId: number;
    serviceId: number;
    startDate: Date;
    endDate?: Date;
};

function createEntityContractMap(deals?: ContractDeal[]): Map<string, Interval[]> {
    const map = new Map<string, Interval[]>();
    deals?.forEach((deal) =>
        deal.entityActiveIntervals.forEach((interval) => {
            const entityId = `${interval.entityId}`;
            const int: Interval = {
                contractId: deal.contractId,
                serviceId: deal.serviceId,
                startDate: interval.startDate,
                endDate: interval.endDate
            };
            if (map.has(entityId)) {
                map.get(entityId)?.push(int);
            } else {
                map.set(entityId, [int]);
            }
        })
    );
    return map;
}

function removeEntitiesWithoutDeals(
    entityIdMap: Map<string, string>,
    entityContractMap: Map<string, Interval[]>
): Map<string, string> {
    const filtered = new Map<string, string>();
    Array.from(entityIdMap.entries()).forEach(([id, name]) => {
        if (entityContractMap.has(id)) {
            filtered.set(id, name);
        }
    });
    return filtered;
}

const UNKNOWN_LABEL = '<unknown>';

function createSelections(
    entityContractMap: Map<string, Interval[]>,
    contractIdMap: Map<string, string>,
    entityIdMap: Map<string, string>,
    serviceIdMap: Map<number, string>
): SelectionRow[] {
    return Array.from(entityContractMap.entries())
        .flatMap(([entityId, intervals]) =>
            intervals.map((interval) => ({
                entityId,
                ...interval
            }))
        )
        .map((interval, index) => ({
            rowId: index,
            ...interval,
            contractName: contractIdMap.get(`${interval.contractId}`) ?? UNKNOWN_LABEL,
            entityName: entityIdMap.get(interval.entityId) ?? UNKNOWN_LABEL,
            serviceName: serviceIdMap.get(interval.serviceId) ?? UNKNOWN_LABEL
        }));
}

function filterSelections(
    options: SelectionRow[],
    entityId: string | undefined,
    serviceId: number | undefined,
    timestamp: string | undefined
): SelectionRow[] {
    const searchDate = isNil(timestamp) ? undefined : new Date(`${timestamp} UTC`);

    return options
        .filter((option) =>
            [
                isNil(entityId) || option.entityId === entityId,
                isNil(serviceId) || option.serviceId === serviceId,
                isNil(searchDate) || isActive(searchDate, option)
            ].reduce((prev, cur) => prev && cur, true)
        )
        .map((option, index) => ({ ...option, rowId: index }));
}

function isActive(searchDate: Date, interval: Interval): boolean {
    return interval.startDate <= searchDate && (isNil(interval.endDate) || interval.endDate >= searchDate);
}
